import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  fetchAdminDashboardMetrics,
  selectAdminDashboardMetrics,
  selectAdminDashboardLoading,
  selectAdminDashboardError,
} from '../../../store/engagementAnalyticsSlice';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const DEPT_COLORS = ['#4B7CF5', '#7C5CBF', '#22C55E', '#EC4899', '#F97316', '#EAB308'];
const DAY_COLORS = ['#22C55E', '#E5E7EB', '#4B7CF5', '#7C5CBF', '#2DD4BF', '#EC4899', '#F97316'];

const deptChartOptions = {
  responsive: true,
  plugins: { legend: { display: false }, tooltip: { enabled: true } },
  scales: {
    x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 12 } } },
    y: {
      grid: { color: '#F3F4F6' },
      border: { display: false },
      ticks: { callback: (v) => `${v}`, font: { size: 12 } },
    },
  },
};

const dayChartOptions = {
  responsive: true,
  plugins: { legend: { display: false }, tooltip: { enabled: true } },
  scales: {
    x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 12 } } },
    y: {
      grid: { color: '#F3F4F6' },
      border: { display: false },
      ticks: { callback: (v) => `${v}h`, font: { size: 12 } },
    },
  },
};

function buildDeptChart(labels = [], values = []) {
  return {
    labels: labels.length ? labels : ['—'],
    datasets: [
      {
        data: values.length ? values : [0],
        backgroundColor: DEPT_COLORS,
        borderRadius: 4,
        barThickness: 24,
      },
    ],
  };
}

function buildDayChart(labels = [], values = []) {
  return {
    labels: labels.length ? labels : ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
    datasets: [
      {
        data: values.length ? values : [0, 0, 0, 0, 0, 0, 0],
        backgroundColor: DAY_COLORS,
        borderRadius: 4,
        barThickness: 24,
      },
    ],
  };
}

function Legend({ labels, colors }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
      {labels.map((label, i) => (
        <div key={`${label}-${i}`} className="flex items-center gap-1">
          <span
            className="inline-block rounded-full"
            style={{ width: 8, height: 8, backgroundColor: colors[i % colors.length] }}
          />
          <span className="text-xs text-[#7E7E7E]">{label}</span>
        </div>
      ))}
    </div>
  );
}

function MetricCard({ icon, title, subtitle, value, chartData, chartOpts, legendLabels, legendColors, loading }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center p-3 rounded-lg bg-[#F6F6F6]">{icon}</div>
        <span className="text-base font-semibold text-[#1D2739]">
          {title}
          {subtitle && <span className="text-base text-[#7E7E7E] font-normal ml-0.5">{subtitle}</span>}
        </span>
      </div>
      <div className="text-[36px] font-semibold text-[#1D2739] mt-1 text-left">
        {loading ? '…' : value}
      </div>
      <div className="mt-1">
        <Bar data={chartData} options={chartOpts} />
      </div>
      <Legend labels={legendLabels} colors={legendColors} />
    </div>
  );
}

const ActiveUsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 12C10.9 12 9.95833 11.6083 9.175 10.825C8.39167 10.0417 8 9.1 8 8C8 6.9 8.39167 5.95833 9.175 5.175C9.95833 4.39167 10.9 4 12 4C13.1 4 14.0417 4.39167 14.825 5.175C15.6083 5.95833 16 6.9 16 8C16 9.1 15.6083 10.0417 14.825 10.825C14.0417 11.6083 13.1 12 12 12ZM4 20V17.2C4 16.6333 4.146 16.1123 4.438 15.637C4.73 15.1617 5.11733 14.8007 5.6 14.554C6.63333 14.0347 7.68333 13.646 8.75 13.388C9.81667 13.13 10.9 13.0007 12 13C13.1 12.9993 14.1833 13.1287 15.25 13.388C16.3167 13.6473 17.3667 14.036 18.4 14.554C18.8833 14.8 19.271 15.1607 19.563 15.636C19.855 16.1113 20.0007 16.6327 20 17.2V20H4Z"
      fill="#1D2739"
    />
  </svg>
);

const SessionIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"
      fill="#1D2739"
    />
  </svg>
);

const SupplierTouchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M9 11.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zm6.5-5H14v1.5h1.5V8H17V6.5h-1.5V5H14v1.5h1.5zM9 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm6.62-.25c.93.7 1.38 1.63 1.38 2.75V19h4v-2.5c0-2.02-3.22-3.19-5.38-3.75z"
      fill="#1D2739"
    />
  </svg>
);

const RfpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H7v-2h5v2zm5-4H7v-2h10v2zm0-4H7V7h10v2z"
      fill="#1D2739"
    />
  </svg>
);

export default function AdminDashboard() {
  const dispatch = useDispatch();
  const metrics = useSelector(selectAdminDashboardMetrics);
  const loading = useSelector(selectAdminDashboardLoading);
  const error = useSelector(selectAdminDashboardError);

  useEffect(() => {
    dispatch(fetchAdminDashboardMetrics('month'));
  }, [dispatch]);

  const activeUsers = metrics?.activeUsers;
  const sessionCount = metrics?.sessionCount;
  const supplierTouches = metrics?.supplierTouches;
  const rfpSourcing = metrics?.rfpSourcing;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-medium text-[#1D2739] text-left">Dashboard</h1>

      {error && (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
          <button
            className="ml-3 underline"
            onClick={() => dispatch(fetchAdminDashboardMetrics('month'))}
          >
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-5">
        <MetricCard
          icon={<ActiveUsersIcon />}
          title="Active Users"
          subtitle=" (Department)"
          value={String(activeUsers?.total ?? 0)}
          chartData={buildDeptChart(activeUsers?.labels, activeUsers?.values)}
          chartOpts={deptChartOptions}
          legendLabels={activeUsers?.labels ?? []}
          legendColors={DEPT_COLORS}
          loading={loading}
        />
        <MetricCard
          icon={<SessionIcon />}
          title="Session count"
          value={`${sessionCount?.totalHours ?? 0}h`}
          chartData={buildDayChart(sessionCount?.labels, sessionCount?.values)}
          chartOpts={dayChartOptions}
          legendLabels={sessionCount?.labels ?? []}
          legendColors={DAY_COLORS}
          loading={loading}
        />
        <MetricCard
          icon={<SupplierTouchIcon />}
          title="Supplier touches"
          subtitle="(Department)"
          value={String(supplierTouches?.total ?? 0)}
          chartData={buildDeptChart(supplierTouches?.labels, supplierTouches?.values)}
          chartOpts={deptChartOptions}
          legendLabels={supplierTouches?.labels ?? []}
          legendColors={DEPT_COLORS}
          loading={loading}
        />
        <MetricCard
          icon={<RfpIcon />}
          title="RFP Sourcing event"
          value={String(rfpSourcing?.total ?? 0)}
          chartData={buildDeptChart(rfpSourcing?.labels, rfpSourcing?.values)}
          chartOpts={deptChartOptions}
          legendLabels={rfpSourcing?.labels ?? []}
          legendColors={DEPT_COLORS}
          loading={loading}
        />
      </div>
    </div>
  );
}
